import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Clock,
  Award,
  CheckCircle2,
  PlayCircle,
  FileText,
  HelpCircle,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  User,
  ArrowRight,
  Lock,
  CreditCard,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Course, CourseModule, Lesson, Enrollment } from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ManualReceiptModal from '../../components/portal/ManualReceiptModal';

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<(CourseModule & { lessons: Lesson[] })[]>([]);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [pendingPayment, setPendingPayment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openModules, setOpenModules] = useState<{ [key: string]: boolean }>({});
  const [enrolling, setEnrolling] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    if (slug) fetchCourseDetails();
  }, [slug, user]);

  const fetchCourseDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Course
      const { data: crs, error: crsErr } = await supabase
        .from('courses')
        .select('*, category:course_categories(*), instructor:profiles(*)')
        .eq('slug', slug)
        .single();

      if (crsErr) {
        console.error('[COURSE DETAIL] Course fetch error:', crsErr);
        // Don't set error yet - could just be not found
      }

      if (crs) {
        setCourse(crs as Course);

        // 2. Fetch Modules & Lessons
        const { data: mods, error: modsErr } = await supabase
          .from('course_modules')
          .select('*, lessons(*)')
          .eq('course_id', crs.id)
          .order('order_index');

        if (modsErr) console.error('[COURSE DETAIL] Modules error:', modsErr);

        if (mods) {
          // Sort lessons inside modules
          const sorted = mods.map((m: any) => ({
            ...m,
            lessons: (m.lessons || []).sort((a: any, b: any) => a.order_index - b.order_index),
          }));
          setModules(sorted);
          // Open first module by default
          if (sorted.length > 0) {
            setOpenModules({ [sorted[0].id]: true });
          }
        }

        // 3. Check if user is enrolled or has pending review payment
        if (user) {
          const { data: enr, error: enrErr } = await supabase
            .from('enrollments')
            .select('*')
            .eq('user_id', user.id)
            .eq('course_id', crs.id)
            .maybeSingle();

          if (enrErr) console.error('[COURSE DETAIL] Enrollment check error:', enrErr);

          if (enr && enr.status === 'Active') {
            setIsEnrolled(true);
            setEnrollment(enr as Enrollment);
          } else {
            // Check if there is a pending receipt submission
            const { data: pay, error: payErr } = await supabase
              .from('payments')
              .select('*')
              .eq('client_id', user.id)
              .eq('course_id', crs.id)
              .eq('status', 'Payment Under Review')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (payErr) console.error('[COURSE DETAIL] Payment check error:', payErr);
            if (pay) {
              setPendingPayment(pay);
            }
          }
        }
      } else {
        // Course not found
        console.warn('[COURSE DETAIL] Course not found for slug:', slug);
      }
    } catch (err) {
      console.error('[COURSE DETAIL] Unexpected error:', err);
      setError('Unable to load course details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (modId: string) => {
    setOpenModules((prev) => ({ ...prev, [modId]: !prev[modId] }));
  };

  const handleEnrollment = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/academy/courses/${slug}` } } });
      return;
    }

    if (!course) return;

    if (course.price > 0 && !isEnrolled) {
      setShowReceiptModal(true);
      return;
    }

    // Direct Free Enrollment
    setEnrolling(true);
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .insert({
          user_id: user.id,
          course_id: course.id,
          status: 'Active',
          progress_percent: 0,
        })
        .select()
        .single();

      if (!error && data) {
        setIsEnrolled(true);
        setEnrollment(data as Enrollment);
        navigate(`/academy/learn/${course.slug}`);
      }
    } catch (err) {
      console.error('Enroll error:', err);
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-24">
          <div className="w-12 h-12 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center pt-24 text-center px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Failed to Load Course</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="flex gap-4">
            <button
              onClick={fetchCourseDetails}
              className="px-6 py-3 bg-darkBlue-600 text-white rounded-xl font-bold text-sm hover:bg-darkBlue-700"
            >
              Try Again
            </button>
            <Link to="/academy" className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200">
              Browse All Courses
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center pt-24 text-center px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-6">The requested course could not be located in our catalog.</p>
          <Link to="/academy" className="px-6 py-3 bg-darkBlue-600 text-white rounded-xl font-bold text-sm">
            Browse All Courses
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const totalLessons = modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Header Banner */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-darkBlue-900 via-darkBlue-800 to-red-800 text-white relative">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-10 items-start justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold">
                  {course.category?.name || 'Academy'}
                </span>
                <span className="text-xs bg-amber-400/20 text-amber-300 border border-amber-400/30 px-2.5 py-0.5 rounded-full font-bold">
                  {course.level}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight mb-4">
                {course.title}
              </h1>

              <p className="text-base sm:text-lg text-blue-100 leading-relaxed mb-6">
                {course.short_description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-blue-200">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-clairBlue-300" />
                  <span>Instructor: {course.instructor?.full_name || 'Bienvenu Masengesho'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-clairBlue-300" />
                  <span>{course.duration_hours} hours total</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-clairBlue-300" />
                  <span>{totalLessons} lessons ({modules.length} modules)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-400" />
                  <span>Official Certificate</span>
                </div>
              </div>
            </div>

            {/* Quick Action Box */}
            <div className="bg-white text-gray-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-gray-100 w-full lg:w-96 flex-shrink-0">
              <div className="mb-6">
                <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider block mb-1">
                  Tuition Fee
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-black text-darkBlue-900">
                    {course.price === 0 ? 'Free' : `$${course.price.toFixed(2)} USD`}
                  </span>
                  {course.price > 0 && (
                    <span className="text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-md">
                      Lifetime Access
                    </span>
                  )}
                </div>
              </div>

              {isEnrolled ? (
                <div className="space-y-3">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-2 text-emerald-800 text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    You are already enrolled in this course!
                  </div>
                  <Link
                    to={`/academy/learn/${course.slug}`}
                    className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <PlayCircle className="w-5 h-5" />
                    <span>Resume Learning</span>
                  </Link>
                </div>
              ) : pendingPayment ? (
                <div className="space-y-3">
                  <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 text-xs space-y-1">
                    <div className="flex items-center gap-2 font-bold text-amber-800">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span>Payment Under Review</span>
                    </div>
                    <p className="text-[11px] text-amber-700 leading-relaxed">
                      Your receipt ({pendingPayment.payment_reference}) was submitted. Access will unlock immediately once verified by our finance team.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowReceiptModal(true)}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-xs transition-colors flex items-center justify-center gap-2"
                  >
                    <span>View / Resubmit Proof</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleEnrollment}
                  disabled={enrolling}
                  className="w-full py-4 bg-gradient-to-r from-darkBlue-600 to-clairBlue-500 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  <span>{course.price === 0 ? 'Enroll for Free' : `Enroll for $${course.price.toFixed(2)}`}</span>
                </button>
              )}

              <div className="mt-6 pt-6 border-t border-gray-100 space-y-3 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>Interactive quizzes and practical exams</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>Verifiable Certificate with unique QR code</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>Mobile & low-bandwidth optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Curriculum */}
      <main className="py-12">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left Column: Outcomes, Description, Curriculum */}
            <div className="lg:col-span-2 space-y-10">
              {/* Learning Outcomes */}
              {course.learning_outcomes && course.learning_outcomes.length > 0 && (
                <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Award className="w-5 h-5 text-clairBlue-600" />
                    What you will learn
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {course.learning_outcomes.map((outcome, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 leading-relaxed">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Course Description */}
              <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Course Overview</h2>
                <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                  {course.description}
                </div>

                {course.prerequisites && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <h3 className="text-sm font-bold text-gray-800 mb-2">Prerequisites</h3>
                    <p className="text-xs text-gray-600">{course.prerequisites}</p>
                  </div>
                )}
              </div>

              {/* Curriculum Modules Accordion */}
              <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Curriculum & Course Content</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      {modules.length} modules • {totalLessons} lessons
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {modules.map((mod) => (
                    <div
                      key={mod.id}
                      className="border border-gray-200 rounded-2xl overflow-hidden transition-all"
                    >
                      <button
                        onClick={() => toggleModule(mod.id)}
                        className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left transition-colors"
                      >
                        <span className="font-bold text-sm text-gray-800">{mod.title}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">
                            {mod.lessons?.length || 0} lessons
                          </span>
                          {openModules[mod.id] ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {openModules[mod.id] && (
                        <div className="divide-y divide-gray-100 bg-white">
                          {(mod.lessons || []).map((lesson) => (
                            <div
                              key={lesson.id}
                              className="px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                {lesson.content_type === 'video' ? (
                                  <PlayCircle className="w-4 h-4 text-clairBlue-600" />
                                ) : lesson.content_type === 'quiz' ? (
                                  <HelpCircle className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <FileText className="w-4 h-4 text-purple-600" />
                                )}
                                <span className="text-xs font-medium text-gray-700">
                                  {lesson.title}
                                </span>
                              </div>

                              <div className="flex items-center gap-3">
                                {lesson.is_free_preview && (
                                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                    Free Preview
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">
                                  {lesson.duration_minutes} min
                                </span>
                                {!isEnrolled && !lesson.is_free_preview && (
                                  <Lock className="w-3.5 h-3.5 text-gray-300" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Instructor & Verification */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 mb-4">Lead Instructor</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-darkBlue-600 to-clairBlue-500 text-white font-bold text-lg flex items-center justify-center">
                    BM
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-gray-800">Bienvenu Masengesho</h4>
                    <p className="text-xs text-gray-500">Founder & Lead Software Architect</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-4">
                  Passionate digital architect and educator with over 6 years of experience helping businesses and students across East Africa harness web tools, business automation, and career opportunities.
                </p>
                <Link
                  to="/bienvenu-masengesho"
                  className="text-xs font-semibold text-clairBlue-600 hover:underline flex items-center gap-1"
                >
                  <span>View Instructor Biography</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              <div className="bg-gradient-to-br from-darkBlue-900 to-darkBlue-800 text-white rounded-3xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldCheck className="w-6 h-6 text-amber-400" />
                  <h3 className="font-bold text-sm">Verified Credentials</h3>
                </div>
                <p className="text-xs text-blue-200 leading-relaxed mb-4">
                  Every certificate issued by BientoMart Academy carries a cryptographically verifiable identifier that employers and organizations can authenticate publicly.
                </p>
                <Link
                  to="/verify-certificate/DEMO-VERIFY"
                  className="text-xs text-amber-300 font-bold hover:underline inline-block"
                >
                  Verify Sample Certificate →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Manual Payment Receipt Modal */}
      {showReceiptModal && (
        <ManualReceiptModal
          isOpen={showReceiptModal}
          onClose={() => setShowReceiptModal(false)}
          course={
            course
              ? {
                  id: course.id,
                  title: course.title,
                  price: course.price,
                  currency: course.currency,
                }
              : null
          }
          existingPayment={pendingPayment}
          onSuccess={() => {
            fetchCourseDetails();
          }}
        />
      )}

      <Footer />
    </div>
  );
}
